// src/hooks/expertDashboardHooks/useExpertActivity.ts
import { useState, useEffect } from 'react';
import { expertActivityApi } from '../api/expert-activity.api';
import { useExpertSocket } from '../connectivity/chat/ExpertSocketProvider';
import { toast } from 'sonner';

export const useExpertActivity = (expertCode: string) => {
  // ─── 1. REST API STATE (Database Toggles) ───
  const [availability, setAvailability] = useState({
    isAvailableChat: false,
    isAvailableCall: false,
    isAvailableVideoCall: false,
  });
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  // ─── 2. WEBSOCKET STATE (Live Presence) ───
  const [liveStatus, setLiveStatus] = useState<string>('offline');
  const { socket } = useExpertSocket();

  // =======================================================
  // 🚀 INITIALIZE DATA (Fetch DB Toggles)
  // =======================================================
  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const data = await expertActivityApi.getAvailability();
        setAvailability({
          isAvailableChat: data.is_available_chat,
          isAvailableCall: data.is_available_call,
          isAvailableVideoCall: data.is_available_video_call,
        });
      } catch (error) {
        console.error('Failed to load availability statuses:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailability();
  }, []);

  // =======================================================
  // 🏓 PRESENCE & HEARTBEAT SWEEPER (WebSockets)
  // =======================================================
  useEffect(() => {
    if (!socket || !expertCode) return;

    // 1. Tell the server we are actively online right now
    socket.emit('expert_online', { expertCode });

    // 2. Start the 5-Second Ping-Pong (Keeps them alive in Redis)
    const pingInterval = setInterval(() => {
      socket.emit('expert_ping', { expertCode });
    }, 5000);

    // 🚀 3. WAKE-UP LOGIC: Only reconnect if the socket physically dropped
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (!socket.connected) {
       
          socket.emit('expert_online', { expertCode });
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 4. Listen for Backend Status Overrides (e.g., getting assigned a user)
    const handlePresenceUpdate = (data: { expertCode: string, status: string }) => {
      if (data.expertCode === expertCode) {
        setLiveStatus(data.status); // Will update to 'busy', 'offline', 'available'
      }
    };

    socket.on('expert_presence_update', handlePresenceUpdate);

    // Cleanup on dismount
    return () => {
      clearInterval(pingInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      socket.off('expert_presence_update', handlePresenceUpdate);
    };
  }, [socket, expertCode]);

  // =======================================================
  // 💾 UPDATE DATABASE TOGGLES (REST API)
  // =======================================================
  const toggleStatus = async (type: 'chat' | 'call' | 'video') => {
    if (isUpdating) return;
    setIsUpdating(true);
    
    const stateKey = 
      type === 'chat' ? 'isAvailableChat' : 
      type === 'call' ? 'isAvailableCall' : 'isAvailableVideoCall';

    const currentStatus = availability[stateKey as keyof typeof availability];
    const newStatus = !currentStatus;

    // 🔥 Optimistic UI Update
    setAvailability(prev => ({ ...prev, [stateKey]: newStatus }));

    const payload: any = {};
    if (type === 'chat') payload.isAvailableChat = newStatus;
    if (type === 'call') payload.isAvailableCall = newStatus;
    if (type === 'video') payload.isAvailableVideoCall = newStatus;

    try {
      const response = await expertActivityApi.toggleAvailability(payload);
      
      setAvailability({
        isAvailableChat: response.availability.is_available_chat,
        isAvailableCall: response.availability.is_available_call,
        isAvailableVideoCall: response.availability.is_available_video_call,
      });
      
      toast.success(`${type.toUpperCase()} availability is now ${newStatus ? 'ON' : 'OFF'}`);
    } catch (error: any) {
      // ⏪ Rollback on error
      setAvailability(prev => ({ ...prev, [stateKey]: currentStatus }));
      toast.error(error.response?.data?.message || `Failed to update ${type} status`);
    } finally {
      setIsUpdating(false);
    }
  };

  const setManualStatus = (status: 'available' | 'away' | 'busy') => {
    if (!socket || !expertCode) return;
    socket.emit('manual_presence_update', { expertCode, status });
    setLiveStatus(status);
  };

  return { 
    availability, 
    liveStatus,
    loading, 
    isUpdating, 
    toggleStatus,
    setManualStatus
  };
};